import React, { useId } from 'react';
import { Acao, Cabecalho, Foto, preencher } from './comum';
import { toneladas } from './gee';
import * as S from './style';

const CORES = ['var(--brand-600)', '#0B6FB0', '#F5A623'];

// A tabela mostra os três últimos anos (cabe no celular com rolagem curta); o
// gráfico de evolução mostra a série inteira.
const ANOS_NA_TABELA = 3;

// Linha suave (Catmull-Rom convertida em Bézier), igual à do protótipo.
const curva = (pts) =>
  pts
    .map((p, k, arr) => {
      if (!k) return `M${p.x} ${p.y}`;
      const a = arr[k - 1];
      const p0 = arr[k - 2] || a;
      const p3 = arr[k + 1] || p;
      const c1x = a.x + (p.x - p0.x) / 6;
      const c1y = a.y + (p.y - p0.y) / 6;
      const c2x = p.x - (p3.x - a.x) / 6;
      const c2y = p.y - (p3.y - a.y) / 6;
      return `C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p.x} ${p.y}`;
    })
    .join(' ');

const GraficoTotais = ({ serie, titulo, unidade }) => {
  const maxBruto = Math.max(...serie.map((p) => p.total), 1);
  const passo = Math.pow(10, Math.floor(Math.log10(maxBruto))) / 2;
  const max = Math.ceil(maxBruto / passo) * passo;
  const yTopo = 26;
  const yBase = 226;
  const xE = 120;
  const xD = 862;
  const yDe = (v) => yBase - (v / max) * (yBase - yTopo);
  const xDe = (k) => (serie.length > 1 ? xE + (k * (xD - xE)) / (serie.length - 1) : (xE + xD) / 2);

  const marcas = Array.from({ length: 6 }, (_, i) => (max / 5) * i);
  const pontos = serie.map((p, k) => ({
    x: +xDe(k).toFixed(1),
    y: +yDe(p.total).toFixed(1),
    dicaX: +Math.min(Math.max(xDe(k), xE - 6), xD - 58).toFixed(1),
    ano: p.ano,
    valor: toneladas(p.total),
  }));
  const linha = curva(pontos);
  const area = `${linha} L${xDe(serie.length - 1).toFixed(1)} ${yBase} L${xDe(0).toFixed(1)} ${yBase} Z`;
  const comUnidade = (v) => [v, unidade].filter(Boolean).join(' ');
  const resumo = `${titulo ? `${titulo}: ` : ''}${pontos.map((p) => `${p.ano}: ${comUnidade(p.valor)}`).join('; ')}`;

  return (
    <S.Grafico viewBox="0 0 900 274" role="img" aria-label={resumo}>
      {marcas.map((v, i) => (
        <g key={i}>
          <line x1={84} x2={880} y1={yDe(v)} y2={yDe(v)} stroke="var(--ink-100)" strokeWidth={1} />
          <text x={74} y={yDe(v) + 4} textAnchor="end" fontSize={13} fill="var(--text-muted)">
            {Math.round(v).toLocaleString('pt-BR')}
          </text>
        </g>
      ))}
      <line x1={84} x2={84} y1={22} y2={yBase} stroke="var(--ink-200)" strokeWidth={1} />
      <line x1={84} x2={880} y1={yBase} y2={yBase} stroke="var(--ink-200)" strokeWidth={1} />
      <path d={area} fill="var(--accent-wash)" />
      <path
        className="linha"
        d={linha}
        pathLength={1}
        fill="none"
        stroke="var(--brand-800)"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {pontos.map((p) => (
        <g key={p.ano} className="ponto" tabIndex={0} role="img" aria-label={`${p.ano}: ${comUnidade(p.valor)}`}>
          <text x={p.x} y={yBase + 26} textAnchor="middle" fontSize={13} fill="var(--text-muted)">
            {p.ano}
          </text>
          <circle className="halo" cx={p.x} cy={p.y} r={11} fill="var(--accent-wash)" stroke="var(--brand-400)" strokeWidth={1} />
          <circle cx={p.x} cy={p.y} r={4.5} fill="var(--brand-800)" />
          <circle cx={p.x} cy={p.y} r={20} fill="transparent" />
          <g className="dica">
            <rect x={p.dicaX - 58} y={p.y - 62} width={116} height={46} rx={8} fill="var(--white)" stroke="var(--border-hairline)" strokeWidth={1} />
            <text x={p.dicaX} y={p.y - 44} textAnchor="middle" fontSize={12} fontWeight={700} letterSpacing=".06em" fill="var(--text-muted)">
              {p.ano}
            </text>
            <text x={p.dicaX} y={p.y - 26} textAnchor="middle" fontSize={14} fontWeight={700} fill="var(--brand-900)">
              {comUnidade(p.valor)}
            </text>
          </g>
        </g>
      ))}
    </S.Grafico>
  );
};

const Numeros = ({ serie, secao, escopos }) => {
  const atual = serie[serie.length - 1];
  const doAno = { ano: atual.ano };
  // id da máscara do SVG: o useId traz ":", que quebra dentro do url(#...).
  const idMascara = `rosca${useId().replace(/:/g, '')}`;
  const anosTabela = serie.slice(-ANOS_NA_TABELA);
  const maior = Math.max(...atual.escopos, 1);

  // Fatias da rosca em porcentagem do anel: onde cada uma começa e quanto ocupa.
  let acumulado = 0;
  const fatias = atual.escopos.map((v, k) => {
    const inicio = acumulado;
    const parte = atual.total ? (v / atual.total) * 100 : 0;
    acumulado += parte;
    return { cor: CORES[k], inicio, parte };
  });

  return (
    <S.GeeDados>
      <div>
        <S.TituloPequeno>
          {secao.tabelaTitulo && <S.H4>{secao.tabelaTitulo}</S.H4>}
          {secao.tabelaSubtitulo && <S.Rotulo>{secao.tabelaSubtitulo}</S.Rotulo>}
        </S.TituloPequeno>
        <S.Tabela>
          <table>
            <thead>
              <tr>
                <th scope="col">{secao.tabelaColuna}</th>
                {anosTabela.map((a) => (
                  <th scope="col" key={a.ano}>
                    {a.ano}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {escopos.map((rotulo, k) => (
                <tr key={k}>
                  <th scope="row">{rotulo}</th>
                  {anosTabela.map((a) => (
                    <td key={a.ano}>{toneladas(a.escopos[k])}</td>
                  ))}
                </tr>
              ))}
              <tr className="total">
                <th scope="row">{secao.totalRotulo}</th>
                {anosTabela.map((a) => (
                  <td key={a.ano}>{toneladas(a.total)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </S.Tabela>
      </div>

      <div>
        <S.TituloPequeno>
          {secao.analiseTitulo && <S.H4>{secao.analiseTitulo}</S.H4>}
          {secao.analiseSubtitulo && <S.Rotulo>{preencher(secao.analiseSubtitulo, doAno)}</S.Rotulo>}
        </S.TituloPequeno>
        <S.GeeGrade>
          <S.GeeCartao>
            <S.TituloPequeno>
              {secao.barrasTitulo && <h4>{secao.barrasTitulo}</h4>}
              {secao.barrasSubtitulo && <S.Rotulo>{preencher(secao.barrasSubtitulo, doAno)}</S.Rotulo>}
            </S.TituloPequeno>
            {escopos.map((rotulo, k) => (
              <S.Barra key={k}>
                <div>
                  <strong>{rotulo}</strong>
                  <span>{toneladas(atual.escopos[k])}</span>
                </div>
                <div>
                  <span style={{ width: `${((atual.escopos[k] / maior) * 100).toFixed(1)}%`, background: CORES[k] }} />
                </div>
              </S.Barra>
            ))}
          </S.GeeCartao>

          <S.GeeCartao>
            <S.TituloPequeno>
              {secao.roscaTitulo && <h4>{secao.roscaTitulo}</h4>}
              {secao.roscaSubtitulo && <S.Rotulo>{preencher(secao.roscaSubtitulo, doAno)}</S.Rotulo>}
            </S.TituloPequeno>
            <S.Rosca>
              <div aria-hidden="true">
                {/* O anel é SVG para a máscara do giro alcançar só ele: com
                    conic-gradient a máscara cortava também o total do centro. */}
                <svg viewBox="0 0 152 152">
                  <defs>
                    <mask id={idMascara}>
                      <circle className="varredura" cx="76" cy="76" r="62" fill="none" stroke="#fff" strokeWidth="30" pathLength="1" />
                    </mask>
                  </defs>
                  <g transform="rotate(-90 76 76)" mask={`url(#${idMascara})`}>
                    {fatias.map((f, k) => (
                      <circle
                        key={k}
                        cx="76"
                        cy="76"
                        r="62.5"
                        fill="none"
                        stroke={f.cor}
                        strokeWidth="27"
                        pathLength="100"
                        strokeDasharray={`${f.parte} 100`}
                        strokeDashoffset={-f.inicio}
                      />
                    ))}
                  </g>
                </svg>
                <div>
                  <p>
                    {secao.totalRotulo && (
                      <>
                        {secao.totalRotulo}
                        <br />
                      </>
                    )}
                    <strong>{toneladas(atual.total)}</strong>
                  </p>
                </div>
              </div>
              <ul>
                {escopos.map((rotulo, k) => (
                  <li key={k}>
                    <span>
                      <i style={{ background: CORES[k] }} />
                      {rotulo}
                    </span>
                    <strong>{atual.total ? Math.round((atual.escopos[k] / atual.total) * 100) : 0}%</strong>
                  </li>
                ))}
              </ul>
            </S.Rosca>
          </S.GeeCartao>
        </S.GeeGrade>
      </div>
    </S.GeeDados>
  );
};

const IconeSelo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8F5F0F" strokeWidth="1.6" aria-hidden="true">
    <circle cx="12" cy="9" r="6" />
    <path d="M8.5 14 7 21l5-2.5L17 21l-1.5-7" />
  </svg>
);

// `serie` são as emissões somadas por ano (gee.emissoesPorAno). Sem dados --
// antes da primeira sincronização do Apps Script -- os números somem e ficam
// só os textos, em vez de uma tabela de zeros.
export const Emissoes = ({ secao, serie }) => {
  const temNumeros = serie.length > 0;
  const atual = serie[serie.length - 1];
  const escopos = [secao.escopo1, secao.escopo2, secao.escopo3];
  const fotos = (secao.eventoFotos || []).filter((f) => f?.foto?.[0]);
  const [principal, ...outras] = fotos;

  return (
    <S.Secao id="emissoes">
      <S.Container>
        <S.GeeTopo>
          <Cabecalho chapeu={secao.chapeu} titulo={secao.titulo} descricao={secao.descricao} markdown />
          {temNumeros && (
            <S.GeeIndicador>
              <strong>{toneladas(atual.total)}</strong>
              {secao.unidade && <span>{secao.unidade}</span>}
              <hr />
              {secao.indicadorRotulo && <S.Rotulo>{preencher(secao.indicadorRotulo, { ano: atual.ano })}</S.Rotulo>}
            </S.GeeIndicador>
          )}
        </S.GeeTopo>

        {temNumeros && <Numeros serie={serie} secao={secao} escopos={escopos} />}

        {serie.length > 1 && (
          <S.Bloco style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', marginTop: 'var(--sp-8)' }}>
            <S.Cabecalho style={{ gap: 'var(--sp-2)', marginBottom: 0 }}>
              {secao.evolucaoTitulo && <S.H3>{secao.evolucaoTitulo}</S.H3>}
              {secao.evolucaoSubtitulo && (
                <S.Rotulo>{preencher(secao.evolucaoSubtitulo, { inicio: serie[0].ano, fim: atual.ano })}</S.Rotulo>
              )}
              {secao.textoEvolucao && <S.Paragrafo>{secao.textoEvolucao}</S.Paragrafo>}
            </S.Cabecalho>
            <GraficoTotais serie={serie} titulo={secao.evolucaoTitulo} unidade={secao.unidade} />
          </S.Bloco>
        )}

        {(secao.eventoTitulo || principal) && (
          <S.Evento>
            <S.Cabecalho style={{ gap: 'var(--sp-2)', marginBottom: 0 }}>
              {secao.eventoChapeu && <S.Chapeu $cor="var(--accent)">{secao.eventoChapeu}</S.Chapeu>}
              {secao.eventoTitulo && <S.H3>{secao.eventoTitulo}</S.H3>}
              {secao.eventoTexto && <S.Paragrafo>{secao.eventoTexto}</S.Paragrafo>}
            </S.Cabecalho>
            {principal && (
              <S.Fotos $n={fotos.length}>
                <figure>
                  <Foto id={principal.foto[0]} largura={1000} alt={principal.legenda} />
                  <figcaption>{principal.legenda}</figcaption>
                </figure>
                {outras.length > 0 && (
                  <div>
                    {outras.map((f, i) => (
                      <figure key={i}>
                        <Foto id={f.foto[0]} largura={1200} alt={f.legenda} />
                        <figcaption>{f.legenda}</figcaption>
                      </figure>
                    ))}
                  </div>
                )}
              </S.Fotos>
            )}
          </S.Evento>
        )}

        {secao.marcoTitulo && (
          <S.Marco>
            <div>
              {secao.marcoChapeu && <p>{secao.marcoChapeu}</p>}
              <h3>
                <IconeSelo />
                {secao.marcoTitulo}
              </h3>
              {secao.marcoTexto && <S.Paragrafo style={{ textAlign: 'left' }}>{secao.marcoTexto}</S.Paragrafo>}
            </div>
            {secao.marcoAno && (
              <div>
                <strong>{secao.marcoAno}</strong>
                <hr />
                {secao.marcoAnoRotulo && <span>{secao.marcoAnoRotulo}</span>}
              </div>
            )}
          </S.Marco>
        )}

        {secao.registro?.rotulo && (
          <S.Registro>
            <div>
              {secao.registroChapeu && <S.Rotulo>{secao.registroChapeu}</S.Rotulo>}
              {secao.registroTexto && <S.Paragrafo>{secao.registroTexto}</S.Paragrafo>}
            </div>
            <Acao acao={secao.registro} variante="primario" />
          </S.Registro>
        )}
      </S.Container>
    </S.Secao>
  );
};
